import { authedFetch } from "./auth.js";
import { readSSEStream } from "./sse-reader.js";

export interface IngestWebProgress {
  stage: string;
  progress: number;
  currentUrl?: string;
  currentTitle?: string;
  totalProcessed?: number;
  chunksCreated?: number;
  linksFound?: number;
}

export interface IngestWebInit {
  sourceId: string;
}

export interface IngestWebCallbacks {
  onInit?: (data: IngestWebInit) => void;
  onProgress?: (data: IngestWebProgress) => void;
  onDone?: (data: { sourceId: string }) => void;
  onError?: (message: string) => void;
}

/**
 * POST /api/ingest/web and consume the SSE progress stream.
 * Returns the sourceId on success, throws on failure.
 */
export async function ingestWeb(
  body: {
    url: string;
    name: string;
    authorityLevel: string;
    projectId?: string;
    crawlConfig?: { maxDepth?: number; maxPages?: number };
    crawlSchedule?: string;
    autoClassify?: boolean;
    category?: string;
    language?: string;
  },
  callbacks?: IngestWebCallbacks,
): Promise<{ sourceId: string }> {
  const res = await authedFetch("/api/ingest/web", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(
      (data as { error?: string }).error ?? `Request failed (${res.status})`,
    );
  }

  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.includes("text/event-stream")) {
    // Fallback for non-SSE responses (shouldn't happen, but safe)
    const data = (await res.json()) as { sourceId: string };
    callbacks?.onDone?.(data);
    return data;
  }

  const reader = res.body?.getReader();
  if (!reader) throw new Error("No response body");

  let sourceId = "";

  await readSSEStream<Record<string, unknown>>(reader, (data, event) => {
    switch (event) {
      case "init":
        sourceId = String(data.sourceId ?? "");
        callbacks?.onInit?.(data as unknown as IngestWebInit);
        break;
      case "progress":
        callbacks?.onProgress?.(data as unknown as IngestWebProgress);
        break;
      case "done":
        sourceId = String(data.sourceId ?? sourceId);
        callbacks?.onDone?.({ sourceId });
        break;
      case "error":
        callbacks?.onError?.(String(data.message ?? "Ingestion failed"));
        return false; // stop reading
    }
  });

  return { sourceId };
}
